<template>
  <div class="">
    <RawRecords v-if="showRawOutput" :records="records" />
    <table v-else-if="processedRecords.length !== 0" class="text-left w-full">
      <colgroup>
        <col class="w-3/4">
        <col class="w-1/4">
      </colgroup>
      <thead>
        <tr class="border-b-2 border-gray-200 text-left">
          <th scope="col" class="py-1.5 font-medium">SOA data</th>
          <th scope="col" class="py-1.5 font-medium">Revalidate in</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(record, index) in processedRecords" :key="index">
          <td>
            <table class="w-full ml-2 border-separate border-spacing-y-1.5">
              <tbody>
                <tr class="group">
                  <th scope="row" class="font-medium">Start of authority</th>
                  <td>
                    <dns-records-link :domain="record.record.host" class="text-blue-600 hover:text-blue-800 hover:underline" />
                    <copy :content="record.record.host" class="[visibility:hidden] group-hover:visible" />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Email</th>
                  <td>{{ adminUserAndHost.user }}@<dns-records-link v-if="domain !== adminUserAndHost.host" :domain="adminUserAndHost.host" class="text-blue-600 hover:text-blue-800 hover:underline" />
                    <span v-else>{{ adminUserAndHost.host }}</span></td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Serial</th>
                  <td>{{ record.record.serial }}</td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Refresh</th>
                  <td>
                    <duration :seconds="record.record.refresh" />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Retry</th>
                  <td>
                    <duration :seconds="record.record.retry" />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Expire</th>
                  <td>
                    <duration :seconds="record.record.expire" />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="font-medium">Negative cache TTL</th>
                  <td>
                    <duration :seconds="record.record.minimum" />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
          <td class="align-top">
            <duration :seconds="record.record.ttl" />
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else>No SOA records found.</div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { useStore } from 'vuex';
import Copy from '@/components/micro/CopyComponent.vue';
import Duration from '@/components/micro/DurationComponent.vue';
import RawRecords from '@/components/micro/RawRecords.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import { QueryResult, RecordInfo, SOA } from '@/model/Api';

const props = defineProps({
  records: {
    type: Object as PropType<QueryResult>,
    required: true,
  },
  domain: {
    type: String,
    required: true,
  },
});

type SoaRecordWithInfo = {
  record: SOA;
} & RecordInfo

// Input:         'aa\.bb\.cc.domain.co.uk
// Match group 1: 'aa\.bb\.cc'
// Match group 3:            'domain.co.uk'
const adminHostRegex = /^((.*\\\.)?[^.]*)\.(.*)/;

const store = useStore();
const showRawOutput = computed(() => store.state.showRawOutput);

const processedRecords = computed(() => props.records.response.answer.filter((item): item is SoaRecordWithInfo => item.record.recordType === 'SOA'));

const adminUserAndHost = computed(() => {
  if (processedRecords.value.length > 0) {
    const match = processedRecords.value[0].record.admin.match(adminHostRegex);
    if (match) {
      return {
        user: match[1].replaceAll('\\.', '.'),
        host: match[3].slice(0, -1), // Remove the trailing dot
      };
    }
  }
  return {
    user: '',
    host: '',
  };
});
</script>
